import React, { useEffect, useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import { createCallLog, searchUsers, updateCallLog } from "../../api/callLogsApi";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import {
  GlobalButton,
  GlobalContactNumberField,
  GlobalDateField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../../global/forms/GlobalFormFields";
import {
  normalizeCallerName,
  validateCallerNameField,
} from "../../validation/callerNameValidation";
import { toCallLogCalendarDateKey } from "../../utils/callLogDates";

const INITIAL_FORM = {
  call_date: "",
  call_time: "",
  caller_name: "",
  contact_number: "",
  call_type: "",
  purpose_id: "",
  is_call_completed: "",
  handled_by: "",
  call_duration: "",
  action_taken: "",
  follow_up_date: "",
  follow_up_assigned_to: "",
  remarks: "",
};

const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
};

const parseDateKeyToLocalDate = (dateKey) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ""))) return null;
  const [y, m, d] = String(dateKey).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

/** Office user search returns role as `department_name` (from mst_roles.role_name). */
function userSearchDeptLabel(user) {
  const raw = user?.department_name ?? user?.role_name;
  if (raw == null || String(raw).trim() === "") return "";
  return String(raw).trim();
}

const AdminCallLogForm = ({
  mode = "create",
  initialValues = null,
  masterData,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [showUserResults, setShowUserResults] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");

  const [followUpAssignUserSearch, setFollowUpAssignUserSearch] = useState("");
  const [followUpAssignUserResults, setFollowUpAssignUserResults] = useState([]);
  const [showFollowUpAssignUserResults, setShowFollowUpAssignUserResults] = useState(false);
  const [searchingFollowUpAssignUsers, setSearchingFollowUpAssignUsers] = useState(false);
  const [selectedFollowUpAssigneeName, setSelectedFollowUpAssigneeName] = useState("");

  const isEditMode = mode === "edit" && !!initialValues?.id;

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (userSearch.length >= 2) {
        setSearchingUsers(true);
        try {
          const resp = await searchUsers(userSearch);
          setUserResults(resp?.data || []);
          setShowUserResults(true);
        } catch (err) {
          console.error("User search failed", err);
        } finally {
          setSearchingUsers(false);
        }
      } else {
        setUserResults([]);
        setShowUserResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [userSearch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (followUpAssignUserSearch.length >= 2) {
        setSearchingFollowUpAssignUsers(true);
        try {
          const resp = await searchUsers(followUpAssignUserSearch);
          setFollowUpAssignUserResults(resp?.data || []);
          setShowFollowUpAssignUserResults(true);
        } catch (err) {
          console.error("Follow-up assignee user search failed", err);
        } finally {
          setSearchingFollowUpAssignUsers(false);
        }
      } else {
        setFollowUpAssignUserResults([]);
        setShowFollowUpAssignUserResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [followUpAssignUserSearch]);

  useEffect(() => {
    if (!isEditMode) {
      setFormData({ ...INITIAL_FORM });
      setSelectedUserName("");
      setUserSearch("");
      setUserResults([]);
      setShowUserResults(false);
      setFollowUpAssignUserSearch("");
      setFollowUpAssignUserResults([]);
      setShowFollowUpAssignUserResults(false);
      setSelectedFollowUpAssigneeName("");
      setErrors({});
      return;
    }

    setFormData({
      call_date: toCallLogCalendarDateKey(initialValues.call_date),
      call_time: initialValues.call_time || "",
      caller_name: initialValues.caller_name || "",
      contact_number: initialValues.contact_number || "",
      call_type:
        initialValues.call_type !== undefined && initialValues.call_type !== null
          ? String(initialValues.call_type)
          : "",
      purpose_id: initialValues.purpose_id || "",
      is_call_completed:
        initialValues.is_call_completed === 1 || initialValues.is_call_completed === true
          ? "true"
          : "false",
      handled_by: initialValues.handled_by || "",
      call_duration: initialValues.call_duration || "",
      action_taken: initialValues.action_taken || "",
      follow_up_date: toCallLogCalendarDateKey(initialValues.follow_up_date),
      follow_up_assigned_to:
        initialValues.follow_up_assigned_to != null && initialValues.follow_up_assigned_to !== ""
          ? String(initialValues.follow_up_assigned_to)
          : "",
      remarks: initialValues.remarks || "",
    });

    setSelectedUserName(
      String(initialValues.handled_by_name || initialValues.handled_by || "").trim()
    );
    setSelectedFollowUpAssigneeName(
      String(initialValues.follow_up_assignee_name || "").trim()
    );
    setUserSearch("");
    setUserResults([]);
    setShowUserResults(false);
    setFollowUpAssignUserSearch("");
    setFollowUpAssignUserResults([]);
    setShowFollowUpAssignUserResults(false);
    setErrors({});
  }, [isEditMode, initialValues]);

  const handleFieldChange = (name) => (value) => {
    const next = name === "caller_name" ? normalizeCallerName(value) : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleCallStatusChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      is_call_completed: value,
      follow_up_date: value === "true" ? "" : prev.follow_up_date,
      follow_up_assigned_to: value === "true" ? "" : prev.follow_up_assigned_to,
    }));
    if (value === "true") {
      setSelectedFollowUpAssigneeName("");
      setFollowUpAssignUserSearch("");
      setFollowUpAssignUserResults([]);
      setShowFollowUpAssignUserResults(false);
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.is_call_completed;
      if (value === "true") {
        delete next.follow_up_date;
        delete next.follow_up_assigned_to;
      }
      return next;
    });
  };

  const handleUserSelect = (user) => {
    setFormData((prev) => ({ ...prev, handled_by: user.id }));
    setSelectedUserName(user.name || "");
    setShowUserResults(false);
    setUserSearch("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.handled_by;
      return next;
    });
  };

  const handleClearUserSelect = () => {
    setFormData((prev) => ({ ...prev, handled_by: "" }));
    setSelectedUserName("");
    setUserSearch("");
  };

  const handleFollowUpAssigneeSelect = (user) => {
    setFormData((prev) => ({ ...prev, follow_up_assigned_to: String(user.id) }));
    setSelectedFollowUpAssigneeName(user.name || "");
    setShowFollowUpAssignUserResults(false);
    setFollowUpAssignUserSearch("");
  };

  const handleClearFollowUpAssignee = () => {
    setFormData((prev) => ({ ...prev, follow_up_assigned_to: "" }));
    setSelectedFollowUpAssigneeName("");
    setFollowUpAssignUserSearch("");
  };

  const validate = () => {
    const next = {};
    if (!formData.call_date) next.call_date = "Call date is required";
    if (!formData.call_time) next.call_time = "Call time is required";
    const callerErr = validateCallerNameField(formData.caller_name);
    if (callerErr) next.caller_name = callerErr;
    if (!formData.contact_number) next.contact_number = "Phone number is required";
    if (!/^[0-9]{10}$/.test(formData.contact_number || "")) {
      next.contact_number = "Phone number must be 10 digits";
    }
    if (formData.call_type === "") next.call_type = "Call type is required";
    if (!formData.purpose_id) next.purpose_id = "Purpose is required";
    if (formData.is_call_completed !== "true" && formData.is_call_completed !== "false") {
      next.is_call_completed = "Select call status";
    }
    const hb = formData.handled_by;
    if (hb === "" || hb === null || hb === undefined) {
      next.handled_by = "Handled by is required";
    }
    if (formData.is_call_completed === "false" && !formData.follow_up_date) {
      next.follow_up_date = "Follow-up date is required when status is needs follow-up";
    }

    if (formData.call_duration !== "" && Number(formData.call_duration) < 0) {
      next.call_duration = "Duration cannot be negative";
    }

    const today = getLocalDateString();
    const callDateObj = parseDateKeyToLocalDate(formData.call_date);
    const todayObj = parseDateKeyToLocalDate(today);
    if (callDateObj && todayObj && callDateObj.getTime() > todayObj.getTime()) {
      next.call_date = "Call date cannot be in the future";
    }
    if (formData.call_date && formData.call_time) {
      const now = new Date();
      const localToday = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      if (formData.call_date === localToday) {
        const [hh = "0", mm = "0"] = String(formData.call_time).split(":");
        const callMinutes = Number(hh) * 60 + Number(mm);
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        if (callMinutes > nowMinutes) {
          next.call_time = "Call time cannot be in the future";
        }
      }
    }
    if (
      formData.is_call_completed === "false" &&
      formData.follow_up_date &&
      formData.follow_up_date < formData.call_date
    ) {
      next.follow_up_date = "Follow-up date cannot be earlier than call date";
    }

    return next;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const followUpAssignedPayload =
        formData.is_call_completed === "true"
          ? null
          : formData.follow_up_assigned_to !== "" && formData.follow_up_assigned_to != null
            ? Number(formData.follow_up_assigned_to)
            : null;

      const payload = {
        ...formData,
        handled_by: formData.handled_by != null && formData.handled_by !== ""
          ? Number(formData.handled_by)
          : formData.handled_by,
        call_duration: formData.call_duration === "" || formData.call_duration == null
          ? "0"
          : String(formData.call_duration),
        action_taken: formData.action_taken || null,
        is_call_completed: formData.is_call_completed === "true",
        follow_up_date:
          formData.is_call_completed === "true" ? null : formData.follow_up_date || null,
        follow_up_assigned_to: followUpAssignedPayload,
      };

      if (mode === "edit" && initialValues?.id) {
        await updateCallLog(initialValues.id, payload, csrfToken);
        onSuccess?.("Call log updated successfully");
      } else {
        await createCallLog(payload, csrfToken);
        onSuccess?.("Call log created successfully");
        setFormData({ ...INITIAL_FORM });
        setSelectedUserName("");
        setUserSearch("");
        setSelectedFollowUpAssigneeName("");
        setFollowUpAssignUserSearch("");
      }
    } catch (err) {
      setErrors({ api: err.message || "Unable to save call log" });
    } finally {
      setLoading(false);
    }
  };

  const submitText =
    loading ? "Saving..." : mode === "edit" ? "Update Call Log" : "Create Call Log";

  const hasExplicitFollowUpAssignee =
    formData.follow_up_assigned_to != null && String(formData.follow_up_assigned_to).trim() !== "";
  const effectiveFollowUpAssigneeDisplay = hasExplicitFollowUpAssignee
    ? selectedFollowUpAssigneeName || "—"
    : selectedUserName || "—";

  return (
    <form onSubmit={handleSubmit} noValidate>
      <GlobalFormShell
        legend={mode === "edit" ? "Edit Call Log" : "Create Call Log"}
        showAlert={Object.keys(errors).length > 0 || !!errors.api}
        alertText={errors.api || "Please fix the highlighted fields."}
        overflowVisible
        gridClassName="gfs-form-grid--3cols"
        actions={
          <>
            <GlobalButton
              type="button"
              variant="primary"
              disabled={loading}
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                void handleSubmit(ev);
              }}
            >
              {submitText}
            </GlobalButton>
            {onCancel ? (
              <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={loading}>
                Cancel
              </GlobalButton>
            ) : null}
          </>
        }
      >
        <GlobalDateField
          label="Call Date *"
          value={formData.call_date}
          onChange={handleFieldChange("call_date")}
          max={getLocalDateString()}
          error={errors.call_date}
        />
        <GlobalTextField
          type="time"
          label="Call Time *"
          value={formData.call_time}
          onChange={handleFieldChange("call_time")}
          error={errors.call_time}
        />
        <GlobalTextField
          label="Caller Name *"
          value={formData.caller_name}
          onChange={handleFieldChange("caller_name")}
          placeholder="Letters only — numbers are not allowed"
          error={errors.caller_name}
        />
        <GlobalContactNumberField
          label="Phone *"
          value={formData.contact_number}
          onChange={handleFieldChange("contact_number")}
          error={errors.contact_number}
        />
        <GlobalSelectField
          label="Call Type *"
          value={formData.call_type}
          onChange={handleFieldChange("call_type")}
          options={(masterData?.types || []).map((t) => ({
            value: String(t.id),
            label: t.type_name || t.label || t.name,
          }))}
          placeholderOption="Select type"
          error={errors.call_type}
        />
        <GlobalSelectField
          label="Call Purpose *"
          value={formData.purpose_id}
          onChange={handleFieldChange("purpose_id")}
          options={(masterData?.purposes || []).map((p) => ({
            value: p.id,
            label: p.purpose_name || p.label || p.name,
          }))}
          placeholderOption="Select purpose"
          error={errors.purpose_id}
        />
        <GlobalSelectField
          label="Call status *"
          value={formData.is_call_completed}
          onChange={handleCallStatusChange}
          options={[
            { value: "true", label: "Completed" },
            { value: "false", label: "Needs follow-up" },
          ]}
          placeholderOption="Select status"
          error={errors.is_call_completed}
        />

        <div style={{ position: "relative" }}>
          <label className="gff-label">
            Handled By <span style={{ color: "#dc2626" }}>*</span>
          </label>

          {formData.handled_by ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.75rem 1rem",
                background: "var(--accent-soft, #f0f9ff)",
                border: "1px solid var(--accent-muted, #bae6fd)",
                borderRadius: "0.5rem",
                marginTop: "0.5rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: "var(--accent, #0ea5e9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {(selectedUserName || "?").charAt(0).toUpperCase()}
                </div>
                <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                  {selectedUserName || "—"}
                </div>
              </div>
              <GlobalButton variant="ghost" size="sm" type="button" onClick={handleClearUserSelect}>
                Change
              </GlobalButton>
            </div>
          ) : (
            <>
              <GlobalTextField
                placeholder="Search name..."
                value={userSearch}
                onChange={setUserSearch}
                error={errors.handled_by}
              />
              {searchingUsers ? (
                <div
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "2.75rem",
                    fontSize: "0.75rem",
                    color: "var(--accent, #0ea5e9)",
                  }}
                >
                  Searching...
                </div>
              ) : null}
              {showUserResults && userResults.length > 0 ? (
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "#fff",
                    border: "1px solid var(--border, #e2e8f0)",
                    width: "100%",
                    zIndex: 10,
                    maxHeight: "200px",
                    overflowY: "auto",
                    borderRadius: "0.5rem",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                    marginTop: "0.25rem",
                  }}
                >
                  {userResults.map((u) => (
                    <div
                      key={u.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleUserSelect(u)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleUserSelect(u);
                        }
                      }}
                      style={{
                        padding: "0.75rem 1rem",
                        cursor: "pointer",
                        borderBottom: "1px solid var(--border, #f1f5f9)",
                        fontSize: "0.875rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</div>
                        {userSearchDeptLabel(u) ? (
                          <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.15rem" }}>
                            {userSearchDeptLabel(u)}
                          </div>
                        ) : null}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--accent, #0ea5e9)", fontWeight: 600 }}>
                        Select
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              {showUserResults && userResults.length === 0 && !searchingUsers && userSearch.length >= 2 ? (
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "#fff",
                    border: "1px solid var(--border, #e2e8f0)",
                    width: "100%",
                    zIndex: 10,
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    marginTop: "0.25rem",
                    fontSize: "0.875rem",
                    color: "#64748b",
                  }}
                >
                  No users found
                </div>
              ) : null}
            </>
          )}
        </div>

        <GlobalTextField
          type="number"
          label="Call Duration (min)"
          value={formData.call_duration}
          onChange={handleFieldChange("call_duration")}
          min={0}
          error={errors.call_duration}
        />

        <div style={{ gridColumn: "1 / -1" }}>
          <GlobalDateField
            label={`Follow-up date${formData.is_call_completed === "false" ? " *" : ""}`}
            value={formData.follow_up_date}
            onChange={handleFieldChange("follow_up_date")}
            min={formData.call_date || undefined}
            disabled={formData.is_call_completed !== "false"}
            error={errors.follow_up_date}
          />
          <fieldset
            disabled={formData.is_call_completed === "true"}
            style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}
          >
          <p
            style={{
              margin: "0.5rem 0 0.35rem",
              fontSize: "0.8125rem",
              color: "#64748b",
            }}
          >
            Assigned to:{" "}
            <span style={{ color: "#334155", fontWeight: 600 }}>{effectiveFollowUpAssigneeDisplay}</span>
          </p>
          <label className="gff-label" style={{ display: "block", marginTop: "0.65rem" }}>
            Assign follow-up to staff
          </label>
          <div style={{ position: "relative", marginTop: "0.35rem" }}>
            {hasExplicitFollowUpAssignee ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem 1rem",
                  background: "var(--accent-soft, #f0f9ff)",
                  border: "1px solid var(--accent-muted, #bae6fd)",
                  borderRadius: "0.5rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "var(--accent, #0ea5e9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {(selectedFollowUpAssigneeName || "?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                    {selectedFollowUpAssigneeName || "—"}
                  </div>
                </div>
                <GlobalButton variant="ghost" size="sm" type="button" onClick={handleClearFollowUpAssignee}>
                  Change
                </GlobalButton>
              </div>
            ) : (
              <>
                <GlobalTextField
                  placeholder="Search staff name..."
                  value={followUpAssignUserSearch}
                  onChange={setFollowUpAssignUserSearch}
                />
                {searchingFollowUpAssignUsers ? (
                  <div
                    style={{
                      position: "absolute",
                      right: "1rem",
                      top: "2.75rem",
                      fontSize: "0.75rem",
                      color: "var(--accent, #0ea5e9)",
                    }}
                  >
                    Searching...
                  </div>
                ) : null}
                {showFollowUpAssignUserResults && followUpAssignUserResults.length > 0 ? (
                  <div
                    style={{
                      position: "absolute",
                      backgroundColor: "#fff",
                      border: "1px solid var(--border, #e2e8f0)",
                      width: "100%",
                      zIndex: 10,
                      maxHeight: "200px",
                      overflowY: "auto",
                      borderRadius: "0.5rem",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      marginTop: "0.25rem",
                    }}
                  >
                    {followUpAssignUserResults.map((u) => (
                      <div
                        key={u.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleFollowUpAssigneeSelect(u)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleFollowUpAssigneeSelect(u);
                          }
                        }}
                        style={{
                          padding: "0.75rem 1rem",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--border, #f1f5f9)",
                          fontSize: "0.875rem",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</div>
                          {userSearchDeptLabel(u) ? (
                            <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.15rem" }}>
                              {userSearchDeptLabel(u)}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--accent, #0ea5e9)", fontWeight: 600 }}>
                          Select
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {showFollowUpAssignUserResults &&
                followUpAssignUserResults.length === 0 &&
                !searchingFollowUpAssignUsers &&
                followUpAssignUserSearch.length >= 2 ? (
                  <div
                    style={{
                      position: "absolute",
                      backgroundColor: "#fff",
                      border: "1px solid var(--border, #e2e8f0)",
                      width: "100%",
                      zIndex: 10,
                      padding: "0.75rem 1rem",
                      borderRadius: "0.5rem",
                      marginTop: "0.25rem",
                      fontSize: "0.875rem",
                      color: "#64748b",
                    }}
                  >
                    No users found
                  </div>
                ) : null}
              </>
            )}
          </div>
          </fieldset>
        </div>

        <GlobalTextAreaField
          label="Action Taken"
          value={formData.action_taken}
          onChange={handleFieldChange("action_taken")}
          rows={4}
          placeholder="Enter action taken"
        />
        <GlobalTextAreaField
          label="Remarks"
          value={formData.remarks}
          onChange={handleFieldChange("remarks")}
          rows={4}
        />
      </GlobalFormShell>
    </form>
  );
};

export default AdminCallLogForm;
